const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.recipegrabber.badrobots.net';

// Token management
let accessToken = null;
let refreshToken = null;

export function setTokens(access, refresh) {
  accessToken = access;
  refreshToken = refresh;
  if (access) {
    localStorage.setItem('accessToken', access);
  } else {
    localStorage.removeItem('accessToken');
  }
  if (refresh) {
    localStorage.setItem('refreshToken', refresh);
  } else {
    localStorage.removeItem('refreshToken');
  }
}

export function loadTokens() {
  accessToken = localStorage.getItem('accessToken');
  refreshToken = localStorage.getItem('refreshToken');
  return { accessToken, refreshToken };
}

export function clearTokens() {
  accessToken = null;
  refreshToken = null;
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
}

// Refresh access token
async function refreshAccessToken() {
  if (!refreshToken) {
    throw new Error('No refresh token');
  }

  const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });

  if (!response.ok) {
    clearTokens();
    throw new Error('Session expired');
  }

  const data = await response.json();
  setTokens(data.accessToken, data.refreshToken);
  return data.accessToken;
}

// API request helper
async function apiRequest(endpoint, options = {}) {
  const makeRequest = async (token) => {
    const headers = {
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    return response;
  };

  let response = await makeRequest(accessToken);

  // If token expired, try to refresh
  if (response.status === 401) {
    // Clone response before reading body, so caller can still read it
    const clonedResponse = response.clone();
    try {
      const data = await clonedResponse.json();
      if (data.code === 'TOKEN_EXPIRED') {
        const newToken = await refreshAccessToken();
        if (newToken) {
          response = await makeRequest(newToken);
        }
      }
    } catch (e) {
      // JSON parse failed, return original response
    }
  }

  return response;
}

// Auth API
export const auth = {
  async login(email, password) {
    const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Login failed');
    }

    setTokens(data.accessToken, data.refreshToken);
    return data;
  },

  async register(email, password, name) {
    const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Registration failed');
    }

    setTokens(data.accessToken, data.refreshToken);
    return data;
  },

  async googleLogin(idToken) {
    const response = await fetch(`${API_BASE_URL}/api/auth/google`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Google login failed');
    }

    setTokens(data.accessToken, data.refreshToken);
    return data;
  },

  async facebookLogin(accessToken) {
    const response = await fetch(`${API_BASE_URL}/api/auth/facebook`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accessToken }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Facebook login failed');
    }

    setTokens(data.accessToken, data.refreshToken);
    return data;
  },

  async appleLogin(identityToken, user) {
    const response = await fetch(`${API_BASE_URL}/api/auth/apple`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ identityToken, user }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Apple login failed');
    }

    setTokens(data.accessToken, data.refreshToken);
    return data;
  },

  async logout() {
    if (refreshToken) {
      await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      }).catch(() => {});
    }
    clearTokens();
  },

  async getMe() {
    const response = await apiRequest('/api/auth/me');
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to get user');
    }

    return data;
  },

  async updateSettings(settings) {
    const response = await apiRequest('/api/auth/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to update settings');
    }

    return data;
  },

  async deleteAccount() {
    const response = await apiRequest('/api/auth/account', {
      method: 'DELETE',
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to delete account');
    }

    clearTokens();
  },

  // Invite methods (public - no auth required)
  async validateInvite(token) {
    const response = await fetch(`${API_BASE_URL}/api/auth/invite/${token}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Invalid invite');
    }

    return data;
  },

  async completeInvite(token, password, name) {
    const response = await fetch(`${API_BASE_URL}/api/auth/invite/${token}/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password, name }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to complete registration');
    }

    setTokens(data.accessToken, data.refreshToken);
    return data;
  },

  // Password management
  async changePassword(currentPassword, newPassword) {
    const response = await apiRequest('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to change password');
    }

    return data;
  },

  async forgotPassword(email) {
    const response = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to send reset email');
    }

    return data;
  },

  async validateResetToken(token) {
    const response = await fetch(`${API_BASE_URL}/api/auth/reset-password/${token}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Invalid reset link');
    }

    return data;
  },

  async resetPassword(token, password) {
    const response = await fetch(`${API_BASE_URL}/api/auth/reset-password/${token}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to reset password');
    }

    return data;
  },
};

// Feedback API
export const feedback = {
  async send(type, message, errorDetails = null) {
    const deviceInfo = {
      platform: 'Web',
      userAgent: navigator.userAgent,
      screenSize: `${window.innerWidth}x${window.innerHeight}`,
      url: window.location.href,
    };

    const response = await apiRequest('/api/feedback', {
      method: 'POST',
      body: JSON.stringify({
        type,
        message,
        errorDetails,
        deviceInfo,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to send feedback');
    }

    return data;
  },
};

// Recipes API
export const recipes = {
  async list(params = {}) {
    const query = new URLSearchParams();
    if (params.search) query.set('search', params.search);
    if (params.folder) query.set('folder', params.folder);
    if (params.favorite) query.set('favorite', 'true');
    if (params.category) query.set('category', params.category);
    if (params.cuisine) query.set('cuisine', params.cuisine);
    if (params.dietary) query.set('dietary', params.dietary);
    if (params.sort) query.set('sort', params.sort);
    if (params.order) query.set('order', params.order);
    if (params.limit) query.set('limit', params.limit);
    if (params.offset) query.set('offset', params.offset);

    const response = await apiRequest(`/api/recipes?${query.toString()}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch recipes');
    }

    return data;
  },

  async get(id) {
    const response = await apiRequest(`/api/recipes/${id}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch recipe');
    }

    return data;
  },

  async create(recipe) {
    const response = await apiRequest('/api/recipes', {
      method: 'POST',
      body: JSON.stringify(recipe),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to create recipe');
    }

    return data;
  },

  async update(id, recipe) {
    const response = await apiRequest(`/api/recipes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(recipe),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to update recipe');
    }

    return data;
  },

  async delete(id) {
    const response = await apiRequest(`/api/recipes/${id}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to delete recipe');
    }
  },

  async toggleFavorite(id) {
    const response = await apiRequest(`/api/recipes/${id}/favorite`, {
      method: 'POST',
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to toggle favorite');
    }

    return data;
  },

  async getByCreator(creatorName, options = {}) {
    const query = new URLSearchParams();
    if (options.excludeRecipeId) query.set('excludeRecipeId', options.excludeRecipeId);
    if (options.limit) query.set('limit', options.limit);

    const encodedCreator = encodeURIComponent(creatorName);
    const queryString = query.toString();
    const response = await apiRequest(`/api/recipes/by-creator/${encodedCreator}${queryString ? '?' + queryString : ''}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch recipes by creator');
    }

    return data;
  },

  async bulkDelete(ids) {
    const response = await apiRequest('/api/recipes/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ recipeIds: ids }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to delete recipes');
    }

    return data;
  },

  async addToFolder(recipeId, folderId) {
    const response = await apiRequest(`/api/recipes/${recipeId}/folders/${folderId}`, {
      method: 'POST',
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to add to folder');
    }
  },

  async removeFromFolder(recipeId, folderId) {
    const response = await apiRequest(`/api/recipes/${recipeId}/folders/${folderId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to remove from folder');
    }
  },

  async export() {
    const response = await apiRequest('/api/recipes/export/json');
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to export recipes');
    }

    return data;
  },

  async import(recipes) {
    const response = await apiRequest('/api/recipes/import', {
      method: 'POST',
      body: JSON.stringify({ recipes }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to import recipes');
    }

    return data;
  },

  async getTags(recipeId) {
    const response = await apiRequest(`/api/recipes/${recipeId}/tags`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to get tags');
    }

    return data;
  },

  async updateTags(recipeId, { categories, cuisines, dietary }) {
    const response = await apiRequest(`/api/recipes/${recipeId}/tags`, {
      method: 'PUT',
      body: JSON.stringify({ categories, cuisines, dietary }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to update tags');
    }

    return data;
  },

  async deleteAiTag(recipeId, type, tag) {
    const response = await apiRequest(`/api/recipes/${recipeId}/ai-tags`, {
      method: 'DELETE',
      body: JSON.stringify({ type, tag }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to delete AI tag');
    }

    return data;
  },

  async acceptAiTag(recipeId, type, tag) {
    const response = await apiRequest(`/api/recipes/${recipeId}/ai-tags/accept`, {
      method: 'POST',
      body: JSON.stringify({ type, tag }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to accept AI tag');
    }

    return data;
  },
};


// Folders API
export const folders = {
  async list() {
    const response = await apiRequest('/api/folders');
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch folders');
    }

    return data.folders;
  },

  async get(id) {
    const response = await apiRequest(`/api/folders/${id}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch folder');
    }

    return data;
  },

  async create(name, color) {
    const response = await apiRequest('/api/folders', {
      method: 'POST',
      body: JSON.stringify({ name, color }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to create folder');
    }

    return data;
  },

  async update(id, updates) {
    const response = await apiRequest(`/api/folders/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to update folder');
    }

    return data;
  },

  async delete(id) {
    const response = await apiRequest(`/api/folders/${id}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to delete folder');
    }
  },

  async addRecipes(folderId, recipeIds) {
    const response = await apiRequest(`/api/folders/${folderId}/recipes`, {
      method: 'POST',
      body: JSON.stringify({ recipeIds }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to add recipes');
    }

    return data;
  },

  async removeRecipes(folderId, recipeIds) {
    const response = await apiRequest(`/api/folders/${folderId}/recipes`, {
      method: 'DELETE',
      body: JSON.stringify({ recipeIds }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to remove recipes');
    }

    return data;
  },
};

// Extract API
export const extract = {
  async fromTranscript(transcript, options = {}) {
    const response = await apiRequest('/api/extract/transcript', {
      method: 'POST',
      body: JSON.stringify({
        transcript,
        sourceUrl: options.sourceUrl,
        sourcePlatform: options.sourcePlatform,
        sourceCreator: options.sourceCreator,
        thumbnailUrl: options.thumbnailUrl,
        autoSave: options.autoSave || false,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to extract recipe');
    }

    return data;
  },

  async improve(recipeId, instructions) {
    const response = await apiRequest(`/api/extract/improve/${recipeId}`, {
      method: 'POST',
      body: JSON.stringify({ instructions }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to improve recipe');
    }

    return data;
  },

  async convert(recipeId, targetSystem) {
    const response = await apiRequest(`/api/extract/convert/${recipeId}`, {
      method: 'POST',
      body: JSON.stringify({ targetSystem }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to convert recipe');
    }

    return data;
  },

  async scale(recipeId, targetServings) {
    const response = await apiRequest(`/api/extract/scale/${recipeId}`, {
      method: 'POST',
      body: JSON.stringify({ targetServings }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to scale recipe');
    }

    return data;
  },
};

// Admin API (requires admin privileges)
export const admin = {
  async getUsers() {
    const response = await apiRequest('/api/admin/users');
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch users');
    }

    return data;
  },

  async getPendingUsers() {
    const response = await apiRequest('/api/admin/users/pending');
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch pending users');
    }

    return data;
  },

  // Invite methods
  async getInvites() {
    const response = await apiRequest('/api/admin/invites');
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch invites');
    }

    return data;
  },

  async createInvite(email, name) {
    const response = await apiRequest('/api/admin/invites', {
      method: 'POST',
      body: JSON.stringify({ email, name }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to create invite');
    }

    return data;
  },

  async resendInvite(inviteId) {
    const response = await apiRequest(`/api/admin/invites/${inviteId}/resend`, {
      method: 'POST',
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to resend invite');
    }

    return data;
  },

  async deleteInvite(inviteId) {
    const response = await apiRequest(`/api/admin/invites/${inviteId}`, {
      method: 'DELETE',
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to delete invite');
    }

    return data;
  },

  async approveUser(userId) {
    const response = await apiRequest(`/api/admin/users/${userId}/approve`, {
      method: 'POST',
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to approve user');
    }

    return data;
  },

  async rejectUser(userId) {
    const response = await apiRequest(`/api/admin/users/${userId}/reject`, {
      method: 'POST',
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to reject user');
    }

    return data;
  },

  async toggleAdmin(userId) {
    const response = await apiRequest(`/api/admin/users/${userId}/toggle-admin`, {
      method: 'POST',
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to toggle admin status');
    }

    return data;
  },

  async togglePremium(userId) {
    const response = await apiRequest(`/api/admin/users/${userId}/toggle-premium`, {
      method: 'POST',
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to toggle premium status');
    }

    return data;
  },

  async revokeUser(userId) {
    const response = await apiRequest(`/api/admin/users/${userId}/revoke`, {
      method: 'POST',
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to revoke user access');
    }

    return data;
  },

  async deleteUser(userId) {
    const response = await apiRequest(`/api/admin/users/${userId}`, {
      method: 'DELETE',
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to delete user');
    }

    return data;
  },

  // Usage & Cost Tracking
  async getUsageDashboard() {
    const response = await apiRequest('/api/admin/usage/dashboard');
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch usage dashboard');
    }

    return data;
  },

  async getUsageEvents(params = {}) {
    const query = new URLSearchParams();
    if (params.limit) query.set('limit', params.limit);
    if (params.offset) query.set('offset', params.offset);
    if (params.eventType) query.set('eventType', params.eventType);
    if (params.platform) query.set('platform', params.platform);
    if (params.success !== undefined) query.set('success', params.success);
    if (params.startDate) query.set('startDate', params.startDate);
    if (params.endDate) query.set('endDate', params.endDate);

    const queryString = query.toString();
    const response = await apiRequest(`/api/admin/usage/events${queryString ? '?' + queryString : ''}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch usage events');
    }

    return data;
  },

  async getUsageProjection() {
    const response = await apiRequest('/api/admin/usage/projection');
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch usage projection');
    }

    return data;
  },
};

// Meal Plan API
export const mealplan = {
  async getWeek(weekDate) {
    const query = weekDate ? `?week=${weekDate}` : '';
    const response = await apiRequest(`/api/mealplan${query}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch meal plan');
    }

    return data;
  },

  async addMeal(date, mealType, recipeId) {
    const response = await apiRequest('/api/mealplan', {
      method: 'POST',
      body: JSON.stringify({ date, mealType, recipeId }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to add meal');
    }

    return data;
  },

  async removeMeal(mealPlanId) {
    const response = await apiRequest(`/api/mealplan/${mealPlanId}`, {
      method: 'DELETE',
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to remove meal');
    }

    return data;
  },

  async clearWeek(weekDate) {
    const response = await apiRequest(`/api/mealplan/week/${weekDate}`, {
      method: 'DELETE',
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to clear week');
    }

    return data;
  },

  async getRecipes(search) {
    const query = search ? `?search=${encodeURIComponent(search)}` : '';
    const response = await apiRequest(`/api/mealplan/recipes${query}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch recipes');
    }

    return data;
  },
};

// Discovery API
export const discover = {
  async getSummary() {
    const response = await apiRequest('/api/discover/summary');
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch discovery summary');
    }

    return data;
  },

  async getCategories() {
    const response = await apiRequest('/api/discover/categories');
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch categories');
    }

    return data;
  },

  async getCuisines() {
    const response = await apiRequest('/api/discover/cuisines');
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch cuisines');
    }

    return data;
  },

  async getDietary() {
    const response = await apiRequest('/api/discover/dietary');
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch dietary');
    }

    return data;
  },

  async getByCategory(category, params = {}) {
    const query = new URLSearchParams();
    if (params.limit) query.set('limit', params.limit);
    if (params.offset) query.set('offset', params.offset);

    const encodedCategory = encodeURIComponent(category);
    const queryString = query.toString();
    const response = await apiRequest(`/api/discover/by-category/${encodedCategory}${queryString ? '?' + queryString : ''}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch recipes');
    }

    return data;
  },

  async getByCuisine(cuisine, params = {}) {
    const query = new URLSearchParams();
    if (params.limit) query.set('limit', params.limit);
    if (params.offset) query.set('offset', params.offset);

    const encodedCuisine = encodeURIComponent(cuisine);
    const queryString = query.toString();
    const response = await apiRequest(`/api/discover/by-cuisine/${encodedCuisine}${queryString ? '?' + queryString : ''}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch recipes');
    }

    return data;
  },

  async getByDietary(dietary, params = {}) {
    const query = new URLSearchParams();
    if (params.limit) query.set('limit', params.limit);
    if (params.offset) query.set('offset', params.offset);

    const encodedDietary = encodeURIComponent(dietary);
    const queryString = query.toString();
    const response = await apiRequest(`/api/discover/by-dietary/${encodedDietary}${queryString ? '?' + queryString : ''}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch recipes');
    }

    return data;
  },

  async getCreators() {
    const response = await apiRequest('/api/discover/creators');
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch creators');
    }

    return data;
  },

  async getCreatorContent(creatorName, platform = 'instagram') {
    const cleanName = creatorName.startsWith('@') ? creatorName.slice(1) : creatorName;
    const encodedCreator = encodeURIComponent(cleanName);
    const response = await apiRequest(`/api/discover/creator/${encodedCreator}?platform=${platform}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch creator content');
    }

    return data;
  },

  async fetchCreatorContent(creatorName, platform = 'instagram') {
    const cleanName = creatorName.startsWith('@') ? creatorName.slice(1) : creatorName;
    const encodedCreator = encodeURIComponent(cleanName);
    const response = await apiRequest(`/api/discover/creator/${encodedCreator}/fetch`, {
      method: 'POST',
      body: JSON.stringify({ platform }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch creator content');
    }

    return data;
  },
};

// Standalone OAuth functions for convenience
export const googleAuth = (idToken) => auth.googleLogin(idToken);
export const facebookAuth = (accessToken) => auth.facebookLogin(accessToken);
export const appleAuth = (identityToken, user) => auth.appleLogin(identityToken, user);
